import { useEffect, useRef, useState } from 'react'
import { RapportDialog } from '@/components/RapportDialog'
import type { CreatePersonalRapportRequest, PersonalRapport } from '@/lib/api'
import { merkeRapport, sichererLink, type RapportAngebot } from '@/lib/rapport'

interface Props {
  employeeId: string
  angebot: RapportAngebot
  mitarbeiterName: string
  /** true, solange noch Stempel in der Outbox auf das Senden warten (Server kennt das Ausstempeln dann noch nicht). */
  stempelAusstehend: boolean
  onAngebot: (a: RapportAngebot) => void
  erstelleRapport: (req: CreatePersonalRapportRequest) => Promise<PersonalRapport>
}

function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine))
  useEffect(() => {
    const an = () => setOnline(true)
    const aus = () => setOnline(false)
    window.addEventListener('online', an)
    window.addEventListener('offline', aus)
    return () => {
      window.removeEventListener('online', an)
      window.removeEventListener('offline', aus)
    }
  }, [])
  return online
}

/** Zusaetzliche Anzeige nach dem Ausstempeln: Link "Rapport generieren", danach der
 * Nextcloud-Link. Es entsteht nichts automatisch (E-520). Greift nicht in den Stempel-Pfad ein. */
export function RapportBereich({
  employeeId,
  angebot,
  mitarbeiterName,
  stempelAusstehend,
  onAngebot,
  erstelleRapport,
}: Props) {
  const online = useOnline()
  const [offen, setOffen] = useState(false)
  const knopfRef = useRef<HTMLButtonElement>(null)

  async function erstellen(signaturen: CreatePersonalRapportRequest['signaturen']) {
    const r = await erstelleRapport({ auftragId: angebot.auftragId, signaturen })
    const rapport = { dateiname: r.dateiname, pfad: r.pfad, link: r.nextcloudLink }
    onAngebot(merkeRapport(employeeId, rapport) ?? { ...angebot, rapport })
    setOffen(false)
  }

  const r = angebot.rapport
  return (
    <section className="rapport-bereich" aria-label="Rapport" style={{ marginTop: 16 }}>
      <p className="hint" style={{ margin: '0 0 6px' }}>
        {angebot.auftragsnummer} — {angebot.auftraggeberName}
      </p>
      {r ? (
        <>
          {sichererLink(r.link) ? (
            <>
              <a href={sichererLink(r.link) ?? undefined} target="_blank" rel="noopener noreferrer">
                Rapport in der Nextcloud öffnen
              </a>
              <p className="hint">Zum Öffnen ist die Anmeldung in der Nextcloud nötig.</p>
            </>
          ) : (
            <p className="confirmation">Rapport abgelegt in {r.pfad}</p>
          )}
        </>
      ) : (
        <>
          <button
            ref={knopfRef}
            className="button button-secondary"
            disabled={!online || stempelAusstehend || offen}
            onClick={() => setOffen(true)}
          >
            Rapport generieren
          </button>
          {!online && (
            <p className="hint">Der Rapport kann nur mit Internetverbindung erstellt werden.</p>
          )}
          {online && stempelAusstehend && (
            <p className="hint">Der Stempel wird noch gesendet. Danach ist der Rapport möglich.</p>
          )}
        </>
      )}
      {offen && (
        <RapportDialog
          auftrag={angebot}
          mitarbeiterName={mitarbeiterName}
          onErstellen={erstellen}
          onSchliessen={() => setOffen(false)}
        />
      )}
    </section>
  )
}
